import type { KeyResult, Objective, Task } from '@portfolio-pm/shared';

/**
 * Deterministic KR and objective rollup maths, factored out of the projection
 * so there is exactly one implementation (REQ-OKR-001, REQ-API-002).
 *
 * The full in-memory projection satisfies {@link RollupSource} structurally, so
 * `createProjection` delegates here. Callers that must not pay for building a
 * whole-workspace projection — notably the Sync Durable Object serving a single
 * objective or key result — supply a bounded, document-backed source instead
 * and get identical numbers.
 */
export interface RollupSource {
  getObjective(id: string): Objective | undefined;
  getKeyResult(id: string): KeyResult | undefined;
  keyResultsByObjective(objectiveId: string): KeyResult[];
  childObjectives(parentObjectiveId: string): Objective[];
  tasksByKeyResult(keyResultId: string): Task[];
}

export function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

export function expectedProgressFromDates(
  startDate: number | undefined,
  endDate: number | undefined,
  now: number,
): number {
  if (startDate === undefined && endDate === undefined) return 0;
  if (startDate !== undefined && now < startDate) return 0;
  if (endDate !== undefined && now >= endDate) return 100;
  if (startDate === undefined || endDate === undefined || startDate >= endDate) return 0;
  return clampPercent(((now - startDate) / (endDate - startDate)) * 100);
}

export function keyResultProgress(source: RollupSource, keyResultId: string): number {
  const keyResult = source.getKeyResult(keyResultId);
  if (!keyResult) return 0;

  switch (keyResult.progressMode) {
    case 'manual':
      return clampPercent(keyResult.manualProgress ?? 0);
    case 'numeric': {
      const start = keyResult.startValue ?? 0;
      const current = keyResult.currentValue ?? 0;
      const target = keyResult.targetValue ?? 0;
      if (target === start) {
        return current >= target ? 100 : 0;
      }
      return clampPercent(((current - start) / (target - start)) * 100);
    }
    case 'task_rollup': {
      const tasks = source.tasksByKeyResult(keyResultId);
      if (tasks.length === 0) return 0;
      const done = tasks.filter((task) => task.completedAt !== undefined).length;
      return clampPercent((done / tasks.length) * 100);
    }
    default:
      return 0;
  }
}

/** Recursive objective rollup with a cycle guard. */
export function objectiveProgress(
  source: RollupSource,
  objectiveId: string,
  stack = new Set<string>(),
): number {
  if (stack.has(objectiveId)) return 0;
  stack.add(objectiveId);

  const ownKrs = source
    .keyResultsByObjective(objectiveId)
    .map((kr) => keyResultProgress(source, kr.id));
  const children = source
    .childObjectives(objectiveId)
    .map((child) => objectiveProgress(source, child.id, new Set(stack)));

  const scores = [...ownKrs, ...children];
  if (scores.length === 0) return 0;
  return clampPercent(scores.reduce((sum, score) => sum + score, 0) / scores.length);
}

export function expectedObjectiveProgress(
  source: RollupSource,
  objectiveId: string,
  now: number,
): number {
  const objective = source.getObjective(objectiveId);
  if (!objective) return 0;
  return expectedProgressFromDates(objective.startDate, objective.endDate, now);
}

export function objectiveLag(source: RollupSource, objectiveId: string, now: number): number {
  return clampPercent(
    expectedObjectiveProgress(source, objectiveId, now) - objectiveProgress(source, objectiveId),
  );
}

export function expectedKeyResultProgress(
  source: RollupSource,
  keyResultId: string,
  now: number,
): number {
  const keyResult = source.getKeyResult(keyResultId);
  if (!keyResult) return 0;
  const objective = source.getObjective(keyResult.objectiveId);
  if (!objective) return 0;
  return expectedProgressFromDates(objective.startDate, objective.endDate, now);
}

export function keyResultLag(source: RollupSource, keyResultId: string, now: number): number {
  return clampPercent(
    expectedKeyResultProgress(source, keyResultId, now) - keyResultProgress(source, keyResultId),
  );
}
